import classNames from "classnames";
import DeviceInternalsConstants from "fleet/constant/device-internals";
import isEqual from "lodash/isEqual";
import PropTypes from "prop-types";
import React from "react";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { Table, Column } from "ui/table";
import { getErrorMessage } from "util/response";

import { ItemCount } from "./item-count";

class DeviceInternalsPage extends React.PureComponent {
  static get propTypes() {
    return {
      device: PropTypes.object,
      fetchInternalState: PropTypes.func.isRequired,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      deviceState: {},
      refreshing: false,
    };

    this._formatDescription = this._formatDescription.bind(this);
    this._formatField = this._formatField.bind(this);
    this._getInternalState = this._getInternalState.bind(this);
    this._retrieveState = this._retrieveState.bind(this);
  }

  componentDidMount() {
    const { device } = this.props;

    if (device) {
      this._retrieveState(device.id);
    }
  }

  componentDidUpdate(prevProps) {
    const { device } = prevProps;

    if (!isEqual(this.props.device, device) && this.props.device) {
      this._retrieveState(this.props.device.id);
    }
  }

  _retrieveState() {
    this.setState({ refreshing: true }, this._getInternalState);
  }

  _getInternalState() {
    const { device, fetchInternalState } = this.props;

    return fetchInternalState(device.id)
      .then((res) => {
        const { data = {} } = res.data;
        this.setState({
          deviceState: data.data,
        });
      })
      .catch((e) => {
        this.setState({
          deviceState: {
            error:
              "Failed to retrieve device state. " +
              "Your device might not be supporting this feature yet.",
            server_reason: getErrorMessage(e),
          },
        });
      })
      .finally(() => {
        this.setState({
          refreshing: false,
        });
      });
  }
  
  _getFieldDescription(field) {
    const allMessageKeys = Object.keys(DeviceInternalsConstants.messages);

    for (const key of allMessageKeys) {
      const re = new RegExp(key);
      if (re.test(field)) {
        return DeviceInternalsConstants.messages[key];
      }
    }

    return null;
  }

  _renderTable() {
    const { deviceState } = this.state;

    const data = Object.keys(deviceState).map((field) => {
      return {
        field,
        description: "Some description",
        expanded: !!this.state[field],
        value: deviceState[field],
      };
    });

    const opts = {
      sortName: "field",
      defaultSortOrder: "asc",
      sizePerPage: 25,
    };

    return (
      <Table
        data={data}
        hover={true}
        keyField="field"
        pagination={true}
        search={true}
        options={opts}
      >
        <Column key="id" dataField="id" isKey={true} hidden={true}>
          ID
        </Column>
        <Column
          key="field"
          dataField="field"
          dataSort={true}
          dataFormat={this._formatField}
          width="50%"
        >
          Field
        </Column>
        <Column
          key="description"
          dataFormat={this._formatDescription}
          width="50%"
        >
          Description
        </Column>
      </Table>
    );
  }

  setFieldState(key) {
    const obj = {
      [key]: !this.state[key],
    };

    this.setState(obj);
  }

  _formatField(field, row) {
    return (
      <div className="formatted-field">
        <div
          key={field}
          onClick={() => this.setFieldState(field)}
          className="field-message-wrapper"
        >
          <i
            className={classNames({
              fa: true,
              "fa-caret-down": row.expanded,
              "fa-caret-right": !row.expanded,
            })}
          />
          <span className="field-message">
            {field}
            <Badge variant="secondary" className="ml-2">
              <ItemCount fieldVal={row.value} />
            </Badge>
          </span>
        </div>
        <pre
          key={"collapse-" + field}
          className={classNames({
            hide: !row.expanded,
            "field-json": true,
          })}
        >
          {JSON.stringify(row.value || {}, null, " ")}
        </pre>
      </div>
    );
  }

  _formatDescription(field, row) {
    return (
      <div className="formatted-field">
        <span className="field-message">
          {this._getFieldDescription(row.field)}
        </span>
      </div>
    );
  }

  render() {
    const { device } = this.props;
    const { refreshing } = this.state;

    if (!device) {
      return null;
    }

    return (
      <Row>
        <Col xs={12}>
          <Button
            variant="info"
            onClick={this._retrieveState}
            disabled={refreshing}
          >
            <i className="fa fa-arrow-circle-o-down" /> Retrieve State
          </Button>
          <br />
          <br />
        </Col>
        <Col xs={12}>{this._renderTable()}</Col>
      </Row>
    );
  }
}

export { DeviceInternalsPage };
